import { useState, useEffect, useCallback } from 'react'
import { X } from 'lucide-react'
import Button from '@/components/ui/Button'
import Input from '@/components/ui/Input'
import KeyNameArrayInput, { type KeyNameItem } from '@/components/ui/KeyNameArrayInput'
import ReportTemplateOrderingList from '@/components/ui/ReportTemplateOrderingList'
import { sanitizeReportSettingsForSave } from '@/components/ui/ReportTemplateOrderingList/utils'
import ReportFieldSettingsModal from './ReportFieldSettingsModal'
import { designTokens } from '@/utils/design-tokens'
import { BusinessService } from '@/api/generated/services/BusinessService'
import type { BusinessResponse } from '@/api/generated/models/BusinessResponse'
import type { ReportSettings } from '@/components/ui/ReportTemplateOrderingList/types'
import { useAlert } from '@/components/ui/Alert/exports'

/** report_settings 블롭의 템플릿별 엔트리 — 순서(sort_key/ignored) + 그 외(프롬프트 등)는 보존 */
type ReportSettingsEntry = { sort_key?: number; ignored?: boolean; [k: string]: unknown }

interface BusinessEditModalProps {
  isOpen: boolean
  onClose: () => void
  onSubmit: (businessData: {
    name: string;
    sortKey?: number;
    available: boolean;
    labels?: Array<{id?: string; key: string; name: string}>;
    detail_fields?: Array<{id?: string; key: string; name: string}>;
    extra_fields?: Array<{id?: string; key: string; name: string}>;
    report_settings?: ReportSettings;
  }) => void
  business: BusinessResponse | null
  isLoading?: boolean
  error?: string | null
}

export default function BusinessEditModal({
  isOpen,
  onClose,
  onSubmit,
  business,
  isLoading = false,
  error = null
}: BusinessEditModalProps) {
  const { showError } = useAlert()
  const [businessName, setBusinessName] = useState('')
  const [sortKey, setSortKey] = useState<string>('')
  const [available, setAvailable] = useState(true)
  const [labels, setLabels] = useState<KeyNameItem[]>([])
  const [detailFields, setDetailFields] = useState<KeyNameItem[]>([])
  const [extraFields, setExtraFields] = useState<KeyNameItem[]>([])
  const [reportSettings, setReportSettings] = useState<ReportSettings>({})
  const [isReportFieldSettingsOpen, setIsReportFieldSettingsOpen] = useState(false)
  // 프롬프트 저장 직후 최신 report_settings 재조회가 진행 중인지 — 이 동안 사업 저장을 막아
  // 옛 프롬프트가 담긴 stale 값이 전송되는 것을 방지 (재조회 완료 전 빠른 저장 가드)
  const [isRefreshingReportSettings, setIsRefreshingReportSettings] = useState(false)

  // Pre-populate form with existing business data
  useEffect(() => {
    if (business) {
      setBusinessName(business.name || '')
      setSortKey(business.sort_key?.toString() || '')
      setAvailable(business.available ?? true)

      // Convert BusinessResponse object arrays to KeyNameItem format
      // TODO: Remove this workaround once backend adds name field to BusinessLabelInfo
      // Currently BusinessLabelInfo only has {id, key} but BusinessDetailFieldInfo and BusinessExtraFieldInfo have {id, key, name}
      // Using key as name temporarily until backend API is updated
      // See: claudedocs/business-label-name-field-design.md
      setLabels(business.labels?.map(item => ({ id: item.id, key: item.key, name: item.key })) || [])
      setDetailFields(business.detail_fields?.map(item => ({ id: item.id, key: item.key, name: item.name || '' })) || [])
      setExtraFields(business.extra_fields?.map(item => ({ id: item.id, key: item.key, name: item.name || '' })) || [])

      // Set report settings (new format)
      if (business.report_settings && typeof business.report_settings === 'object') {
        setReportSettings(business.report_settings as ReportSettings)
      } else {
        setReportSettings({})
      }
    }
  }, [business])

  // Reset form when modal is closed
  useEffect(() => {
    if (!isOpen) {
      setBusinessName('')
      setSortKey('')
      setAvailable(true)
      setLabels([])
      setDetailFields([])
      setExtraFields([])
      setReportSettings({})
      setIsRefreshingReportSettings(false)
    }
  }, [isOpen])

  // Show error alert when error prop changes
  useEffect(() => {
    if (error) {
      showError(error)
    }
  }, [error, showError])

  // 자식(시스템 프롬프팅 설정) 모달에서 프롬프트를 저장하면 백엔드의 report_settings 블롭
  // (순서 sort_key/ignored + 필드 프롬프트가 함께 저장됨)이 갱신된다.
  // 이 모달이 열릴 때 스냅샷한 reportSettings 는 옛 프롬프트를 들고 있어, 그대로 사업 저장 시
  // 최신 프롬프트를 덮어써 롤백되는 문제가 있다.
  // → 프롬프트 저장 직후 최신 report_settings 를 다시 받아오되, 사용자가 편집 중인
  //   순서(sort_key/ignored)는 유지한다. (프롬프트가 어떤 하위 키에 저장되든 무관하게 동작)
  const refreshReportSettings = useCallback(async () => {
    if (!business?.id) return
    setIsRefreshingReportSettings(true)
    try {
      const fresh = await BusinessService.appsBusinessViewsGetBusiness({ businessId: business.id })
      const freshSettings: Record<string, ReportSettingsEntry> =
        fresh?.report_settings && typeof fresh.report_settings === 'object'
          ? (fresh.report_settings as Record<string, ReportSettingsEntry>)
          : {}
      setReportSettings(prev => {
        const prevEntries = prev as unknown as Record<string, ReportSettingsEntry>
        const merged: Record<string, ReportSettingsEntry> = { ...freshSettings }
        // 편집 중인 순서 설정만 최신 프롬프트 위에 덮어써 유지
        // (sort_key 는 유효한 숫자일 때만 복사 — null/NaN 이 전파되면 저장 시 서버 500)
        for (const key of Object.keys(prevEntries)) {
          const cur = prevEntries[key] ?? {}
          merged[key] = { ...(freshSettings[key] ?? {}) }
          if (typeof cur.sort_key === 'number' && Number.isFinite(cur.sort_key)) merged[key].sort_key = cur.sort_key
          if (typeof cur.ignored === 'boolean') merged[key].ignored = cur.ignored
        }
        return merged as unknown as ReportSettings
      })
    } catch (err) {
      // 최신화 실패는 프롬프트 저장 실패가 아니므로 조용히 무시 (다음 열람 시 최신화됨)
      console.error('Failed to refresh report settings after prompt save:', err)
    } finally {
      setIsRefreshingReportSettings(false)
    }
  }, [business?.id])

  const handleSubmit = () => {
    // 프롬프트 재조회가 끝나기 전에는 저장 금지 (stale 프롬프트 전송 방지)
    if (isRefreshingReportSettings) return
    if (businessName.trim()) {
      const sortKeyNumber = sortKey.trim() ? parseInt(sortKey, 10) : undefined
      onSubmit({
        name: businessName.trim(),
        sortKey: !isNaN(sortKeyNumber as number) ? sortKeyNumber : undefined,
        available,
        // 빈 배열도 그대로 전송 — 서버는 목록에 없는 항목을 삭제한다 (undefined 면 변경 없음으로 처리됨)
        labels,
        detail_fields: detailFields,
        extra_fields: extraFields,
        // 전송 직전 정화 — 유효하지 않은 sort_key(null 등)는 키를 제거해 서버 오염/500 방지
        report_settings: Object.keys(reportSettings).length > 0 ? sanitizeReportSettingsForSave(reportSettings) : undefined
      })
    }
  }

  const handleClose = () => {
    setBusinessName('')
    setSortKey('')
    setAvailable(true)
    setLabels([])
    setDetailFields([])
    setExtraFields([])
    setReportSettings({})
    onClose()
  }

  if (!isOpen) return null

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center"
      data-modal-overlay
      onMouseDown={(e) => e.stopPropagation()}
    >
      {/* Background Blur Overlay */}
      <div
        className="absolute inset-0"
        style={{ backgroundColor: 'rgba(0, 0, 0, 0.2)' }}
        onClick={handleClose}
      />

      {/* Modal Content */}
      <div
        className="relative bg-white rounded w-[600px] max-w-[90vw] max-h-[90vh] overflow-auto border"
        style={{
          borderColor: designTokens.colors.gray[1]
        }}
      >
        <div className="flex flex-col gap-4 p-[30px]">
          {/* Header */}
          <div className="flex items-center justify-between gap-[10px] py-[7px]">
            <h2
              className="font-bold"
              style={{
                fontFamily: designTokens.typography.fontFamily.sans[0],
                fontSize: designTokens.typography.fontSize['title-medium'][0],
                lineHeight: designTokens.typography.fontSize['title-medium'][1].lineHeight,
                fontWeight: designTokens.typography.fontSize['title-medium'][1].fontWeight,
                color: designTokens.colors.gray[6]
              }}
            >
              사업 수정
            </h2>
            <button
              type="button"
              onClick={handleClose}
              className="w-6 h-6 flex items-center justify-center hover:opacity-70 transition-opacity"
              style={{ color: designTokens.colors.gray[5] }}
            >
              <X className="w-3 h-3" strokeWidth={2} />
            </button>
          </div>


          {/* Content */}
          <div className="flex flex-col gap-6">
            {/* Form Section */}
            <div className="flex flex-col gap-5">
              <div className="flex flex-col gap-[10px]">
                {/* Business Name Input */}
                <div className="flex flex-col gap-[10px]">
                  <div className="flex items-center gap-5">
                    <div className="w-[150px]">
                      <label
                        style={{
                          fontFamily: designTokens.typography.fontFamily.sans[0],
                          fontSize: designTokens.typography.fontSize['body-medium'][0],
                          lineHeight: designTokens.typography.fontSize['body-medium'][1].lineHeight,
                          fontWeight: designTokens.typography.fontSize['body-medium'][1].fontWeight,
                          color: designTokens.colors.gray[6]
                        }}
                      >
                        사업명
                        <span
                          className="ml-1"
                          style={{
                            fontFamily: designTokens.typography.fontFamily.sans[0],
                            fontSize: designTokens.typography.fontSize['body-xsmall'][0],
                            lineHeight: designTokens.typography.fontSize['body-xsmall'][1].lineHeight,
                            color: designTokens.colors.error.DEFAULT
                          }}
                        >
                          *
                        </span>
                      </label>
                    </div>
                    <div className="flex-1">
                      <Input
                        placeholder="사업명을 입력해주세요."
                        value={businessName}
                        onChange={(e) => setBusinessName(e.target.value)}
                        size="small"
                        className="w-full"
                        autoFocus
                      />
                    </div>
                  </div>
                </div>

                {/* Sort Key Input */}
                <div className="flex flex-col gap-[10px]">
                  <div className="flex items-center gap-5">
                    <div className="w-[150px]">
                      <label
                        style={{
                          fontFamily: designTokens.typography.fontFamily.sans[0],
                          fontSize: designTokens.typography.fontSize['body-medium'][0],
                          lineHeight: designTokens.typography.fontSize['body-medium'][1].lineHeight,
                          fontWeight: designTokens.typography.fontSize['body-medium'][1].fontWeight,
                          color: designTokens.colors.gray[6]
                        }}
                      >
                        순서
                      </label>
                    </div>
                    <div className="flex-1">
                      <Input
                        type="number"
                        placeholder="순서를 입력해주세요. (선택사항)"
                        value={sortKey}
                        onChange={(e) => setSortKey(e.target.value)}
                        size="small"
                        className="w-full"
                        min="1"
                      />
                    </div>
                  </div>
                </div>

                {/* Status Toggle */}
                <div className="flex flex-col gap-[10px]">
                  <div className="flex items-center gap-5">
                    <div className="w-[150px]">
                      <label
                        style={{
                          fontFamily: designTokens.typography.fontFamily.sans[0],
                          fontSize: designTokens.typography.fontSize['body-medium'][0],
                          lineHeight: designTokens.typography.fontSize['body-medium'][1].lineHeight,
                          fontWeight: designTokens.typography.fontSize['body-medium'][1].fontWeight,
                          color: designTokens.colors.gray[6]
                        }}
                      >
                        상태
                        <span
                          className="ml-1"
                          style={{
                            fontFamily: designTokens.typography.fontFamily.sans[0],
                            fontSize: designTokens.typography.fontSize['body-xsmall'][0],
                            lineHeight: designTokens.typography.fontSize['body-xsmall'][1].lineHeight,
                            color: designTokens.colors.error.DEFAULT
                          }}
                        >
                          *
                        </span>
                      </label>
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-4">
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="radio"n
                            name="status"
                            checked={available}
                            onChange={() => setAvailable(true)}
                            className="w-4 h-4"
                            style={{ accentColor: designTokens.colors.primary.DEFAULT }}
                          />
                          <span
                            style={{
                              fontFamily: designTokens.typography.fontFamily.sans[0],
                              fontSize: designTokens.typography.fontSize['body-medium'][0],
                              lineHeight: designTokens.typography.fontSize['body-medium'][1].lineHeight,
                              fontWeight: designTokens.typography.fontSize['body-medium'][1].fontWeight,
                              color: designTokens.colors.gray[6]
                            }}
                          >
                            활성
                          </span>
                        </label>
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="radio"
                            name="status"
                            checked={!available}
                            onChange={() => setAvailable(false)}
                            className="w-4 h-4"
                            style={{ accentColor: designTokens.colors.primary.DEFAULT }}
                          />
                          <span
                            style={{
                              fontFamily: designTokens.typography.fontFamily.sans[0],
                              fontSize: designTokens.typography.fontSize['body-medium'][0],
                              lineHeight: designTokens.typography.fontSize['body-medium'][1].lineHeight,
                              fontWeight: designTokens.typography.fontSize['body-medium'][1].fontWeight,
                              color: designTokens.colors.gray[6]
                            }}
                          >
                            비활성
                          </span>
                        </label>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Labels Input */}
                <div className="flex flex-col gap-[10px]">
                  <KeyNameArrayInput
                    label="라벨"
                    values={labels}
                    onChange={setLabels}
                    keyPlaceholder="라벨 키를 입력해주세요"
                    disabled={isLoading}
                    keyOnly={true}
                  />
                </div>

                {/* Detail Fields Input */}
                <div className="flex flex-col gap-[10px]">
                  <KeyNameArrayInput
                    label="상세 필드"
                    values={detailFields}
                    onChange={setDetailFields}
                    keyPlaceholder="필드 키를 입력해주세요"
                    namePlaceholder="필드 이름을 입력해주세요"
                    disabled={isLoading}
                  />
                </div>

                {/* Extra Fields Input */}
                <div className="flex flex-col gap-[10px]">
                  <KeyNameArrayInput
                    label="추가 필드"
                    values={extraFields}
                    onChange={setExtraFields}
                    keyPlaceholder="필드 키를 입력해주세요"
                    namePlaceholder="필드 이름을 입력해주세요"
                    disabled={isLoading}
                  />
                </div>

                {/* Report Settings */}
                <div className="flex flex-col gap-[10px]">
                  <ReportTemplateOrderingList
                    reportSettings={reportSettings}
                    onSettingsChange={setReportSettings}
                    disabled={isLoading}
                    headerAction={
                      <Button
                        color="light"
                        size="small"
                        onClick={() => setIsReportFieldSettingsOpen(true)}
                        disabled={isLoading || !business?.id}
                        className="px-[10px] h-8"
                      >
                        시스템 프롬프팅 설정
                      </Button>
                    }
                  />
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex items-center justify-end gap-[10px]">
              <Button
                color="light"
                size="medium"
                onClick={handleClose}
                disabled={isLoading}
                className="px-[10px] h-10"
              >
                취소
              </Button>
              <Button
                color="dark"
                size="medium"
                onClick={handleSubmit}
                disabled={isLoading || isRefreshingReportSettings || !businessName.trim()}
                className="px-[10px] h-10"
              >
                {isLoading ? '저장 중...' : isRefreshingReportSettings ? '동기화 중...' : '저장'}
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* 시스템 프롬프팅(필드별 프롬프트) 설정 모달 */}
      {business?.id && (
        <ReportFieldSettingsModal
          isOpen={isReportFieldSettingsOpen}
          onClose={() => setIsReportFieldSettingsOpen(false)}
          onSaved={refreshReportSettings}
          businessId={business.id}
          businessName={business.name}
        />
      )}
    </div>
  )
}